import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Sparkles, Lock, Plus, Trash2, Users, CreditCard,
} from "lucide-react";
import { PILL, Field, Input } from "./shared";

export default function TeamTab({ biz, onUpgrade }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [form, setForm] = useState({ email: "", role: "manager" });

  const load = async () => {
    try {
      const { data: d } = await api.get("/team/members");
      setData(d);
    } catch { toast.error("Couldn't load team"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e?.preventDefault();
    if (!form.email.includes("@")) { toast.error("Valid email required"); return; }
    setInviting(true);
    try {
      const origin_url = window.location.origin;
      const { data: res } = await api.post("/team/invite", { ...form, origin_url });
      if (res.email_sent) toast.success(`Invite emailed to ${res.email}`);
      else toast.success("Invite created — share the link manually");
      setForm({ email: "", role: "manager" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite");
    }
    setInviting(false);
  };

  const revoke = async (invite_id) => {
    if (!window.confirm("Revoke this invite?")) return;
    setRevokingId(invite_id);
    try { await api.delete(`/team/invite/${invite_id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't revoke"); }
    setRevokingId(null);
  };

  const removeMember = async (user_id) => {
    if (!window.confirm("Remove this team member from the business?")) return;
    setRemovingId(user_id);
    try { await api.delete(`/team/members/${user_id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
    setRemovingId(null);
  };

  if (loading) return <div className="nb-card-flat p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  const seatsAvailable = data?.seats_available || 0;
  const seatsUsed = data?.seats_used || 0;
  const pending = data?.pending_invites || [];
  const seatsLeft = Math.max(0, seatsAvailable - seatsUsed - pending.length);
  const canManage = data?.can_manage;
  const noSeats = seatsAvailable === 0;

  return (
    <div>
      <div className="nb-card p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl font-extrabold flex items-center gap-2"><Users className="w-6 h-6" /> Team Members</div>
          <div className="text-sm text-gray-600 mt-1">
            Invite people to help manage <b>{biz?.summary?.name || "this website"}</b>.
            {noSeats ? " Your plan doesn't include team seats — upgrade to invite teammates." : ` Plan includes ${seatsAvailable} seat${seatsAvailable === 1 ? "" : "s"}.`}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-extrabold leading-none">{seatsUsed + pending.length}<span className="text-gray-400">/{seatsAvailable}</span></div>
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500 mt-1">seats used</div>
        </div>
      </div>

      {data?.owner && (
        <div className="nb-card p-4 mb-4 flex items-center gap-3" data-testid="team-owner-row">
          <div className="w-10 h-10 rounded-lg bg-[#10B981] text-white border-2 border-black flex items-center justify-center font-extrabold">{(data.owner.name || data.owner.email || "?").charAt(0).toUpperCase()}</div>
          <div className="flex-1">
            <div className="font-bold">{data.owner.name} <span className="text-[10px] font-extrabold bg-[#10B981] text-white px-1.5 py-0.5 rounded ml-1">OWNER</span></div>
            <div className="text-xs text-gray-600">{data.owner.email}</div>
          </div>
        </div>
      )}

      {(data?.members || []).length > 0 && (
        <div className="space-y-3 mb-6">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 px-1">Active members</div>
          {data.members.map((m) => (
            <div key={m.user_id} className="nb-card p-4 flex items-center gap-3" data-testid={`team-member-${m.user_id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#F5F0FF] border-2 border-black flex items-center justify-center font-extrabold">{(m.name || m.email || "?").charAt(0).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold flex items-center gap-2 flex-wrap">{m.name || m.email}
                  <span className="text-[10px] font-extrabold bg-[#8B5CF6] text-white px-1.5 py-0.5 rounded uppercase">{m.role}</span>
                </div>
                <div className="text-xs text-gray-600">{m.email} · joined {new Date(m.added_at).toLocaleDateString()}</div>
              </div>
              {canManage && (
                <button onClick={() => removeMember(m.user_id)} disabled={removingId === m.user_id}
                  className={`${PILL} bg-white`} data-testid={`remove-member-${m.user_id}`}>
                  {removingId === m.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div className="space-y-3 mb-6">
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-gray-500 px-1">Pending invites</div>
          {pending.map((p) => (
            <div key={p.invite_id} className="nb-card p-4 flex items-center gap-3 bg-[#FFF7E6]" data-testid={`team-invite-${p.invite_id}`}>
              <div className="w-10 h-10 rounded-lg bg-white border-2 border-black flex items-center justify-center"><Loader2 className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{p.email}
                  <span className="text-[10px] font-extrabold bg-amber-500 text-white px-1.5 py-0.5 rounded ml-2 uppercase">{p.role}</span>
                  <span className="text-[10px] font-bold text-amber-700 ml-2">PENDING</span>
                </div>
                <div className="text-xs text-gray-600">expires {new Date(p.expires_at).toLocaleDateString()}</div>
              </div>
              {canManage && (
                <button onClick={() => revoke(p.invite_id)} disabled={revokingId === p.invite_id}
                  className={`${PILL} bg-white`} data-testid={`revoke-invite-${p.invite_id}`}>
                  {revokingId === p.invite_id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Revoke"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage && (
        noSeats ? (
          <div className="nb-card p-6 bg-[#F5F0FF] text-center" data-testid="team-upgrade-banner">
            <Lock className="w-8 h-8 mx-auto mb-3" />
            <div className="font-display text-xl font-extrabold mb-1">Upgrade to add teammates</div>
            <p className="text-sm text-gray-700 mb-4">Launch plan includes 1 seat, Growth includes 2, Scale includes 3.</p>
            <button onClick={onUpgrade} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="team-upgrade-btn">
              <CreditCard className="w-4 h-4" /> View plans
            </button>
          </div>
        ) : seatsLeft > 0 ? (
          <form onSubmit={invite} className="nb-card p-5 space-y-3" data-testid="team-invite-form">
            <div className="font-bold flex items-center gap-2"><Plus className="w-4 h-4" /> Invite a teammate</div>
            <Field label="Their email">
              <Input type="email" required value={form.email} testId="team-invite-email"
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="teammate@example.com" />
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="team-invite-role"
                className="w-full px-4 py-3 border-2 border-black rounded-xl bg-white font-semibold">
                <option value="manager">Manager (edit content & settings)</option>
              </select>
            </Field>
            <button disabled={inviting} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="team-send-invite-btn">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Send invite</>}
            </button>
            <p className="text-xs text-gray-500">An email with an accept link will be sent. Manager access: edit pages, content, and settings (no billing or destructive actions).</p>
          </form>
        ) : (
          <div className="nb-card-flat p-5 text-sm text-center bg-[#FFF7E6]" data-testid="team-no-seats-left">
            All {seatsAvailable} seat{seatsAvailable === 1 ? "" : "s"} used. Remove a teammate to invite a new one — or upgrade your plan.
          </div>
        )
      )}
    </div>
  );
}
